// apps/client/src/components/brokers/RevenueHurdleSchedule.tsx
//
// Month-by-month monthly revenue hurdle schedule. Shared by the admin
// Commission Settings page and the broker's own commission page so the two
// surfaces render the same numbers the same way. Rendered only when the
// broker's config history carries a hurdle; without one the pages are
// byte-identical to before.
//
// Maths: utils/revenueHurdle.ts. Handover:
// docs/handovers/2026-10-06-COMMISSION-MONTHLY-REVENUE-HURDLE.md

import React, { useMemo, useState } from 'react';
import type { HurdleMonth } from '../../utils/revenueHurdle';
import { fiscalYearLabel, monthLabel } from '../../utils/commissionTree';
import { formatCurrencyShorthand } from '../../utils/formatters';

interface Props {
  schedule: HurdleMonth[];
  /** Which share the payout column is built from, for the subtitle. */
  shareBasisLabel: string;
}

function fullCents(cents: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(cents / 100);
}

function Money({ cents, className }: { cents: number; className?: string }) {
  return (
    <span className={className} title={fullCents(cents)}>
      {formatCurrencyShorthand(cents)}
    </span>
  );
}

function factorLabel(factor: number | null): string {
  if (factor === null) return 'n/a';
  return `${(factor * 100).toFixed(1)}%`;
}

const HEADERS_RIGHT = [
  'Attributed Revenue',
  'Hurdle',
  'Factor',
  'Split Payout',
  'Monthly Incentive',
  'FY To Date',
] as const;

const HELP: Record<(typeof HEADERS_RIGHT)[number], string> = {
  'Attributed Revenue':
    'Upfront commission on every deal plus trail on the broker’s own deals, less clawbacks and referrer fees.',
  Hurdle: 'The monthly revenue the broker must clear before the split rate applies.',
  Factor: 'One minus hurdle divided by attributed revenue. Below 100% scales the payout down; below zero means the month is in deficit.',
  'Split Payout': 'The tier-based commission for the month before the hurdle.',
  'Monthly Incentive': 'Split payout multiplied by the factor. A negative month reduces the financial year total.',
  'FY To Date': 'Running total for the financial year, including any carried deficit.',
};

export function RevenueHurdleSchedule({ schedule, shareBasisLabel }: Props) {
  const years = useMemo(() => {
    const map = new Map<number, HurdleMonth[]>();
    for (const m of schedule) {
      const list = map.get(m.fiscalYear);
      if (list) list.push(m);
      else map.set(m.fiscalYear, [m]);
    }
    return Array.from(map.entries())
      .sort(([a], [b]) => b - a)
      .map(([fy, months]) => ({ fy, months: [...months].sort((a, b) => b.month.localeCompare(a.month)) }));
  }, [schedule]);

  const [expanded, setExpanded] = useState<Set<number>>(() => new Set(years[0] ? [years[0].fy] : []));

  return (
    <div className="bg-gray-800 border border-gray-700 rounded-lg overflow-hidden shadow-lg" data-testid="revenue-hurdle-schedule">
      <div className="p-6 border-b border-gray-700 flex items-center justify-between">
        <div>
          <h3 className="text-lg font-bold text-white">Monthly Revenue Hurdle</h3>
          <p className="text-sm text-gray-400 mt-1">
            The split payout ({shareBasisLabel}) is scaled by how far attributed revenue clears the monthly hurdle. Months below the hurdle go negative and net against the financial year.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs text-gray-500 uppercase tracking-wider font-semibold">Legend:</span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[0.625rem] font-semibold bg-green-500/10 text-green-400">Above hurdle</span>
          <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-[0.625rem] font-semibold bg-red-500/10 text-red-400">Below hurdle</span>
        </div>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-900 border-b border-gray-700">
            <tr>
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Month</th>
              {HEADERS_RIGHT.map(h => (
                <th key={h} className="px-6 py-4 text-right text-xs font-semibold text-gray-300 uppercase tracking-wider" title={HELP[h]}>{h}</th>
              ))}
              <th className="px-6 py-4 text-left text-xs font-semibold text-gray-300 uppercase tracking-wider">Note</th>
            </tr>
          </thead>
          <tbody>
            {schedule.length === 0 && (
              <tr><td colSpan={8} className="px-6 py-8 text-center text-gray-500">No commission months loaded yet.</td></tr>
            )}
            {years.map(({ fy, months }) => {
              const isExpanded = expanded.has(fy);
              const latest = months[0];
              return (
                <React.Fragment key={fy}>
                  <tr
                    className="bg-gray-700/30 border-b border-gray-700 cursor-pointer hover:bg-gray-700/50 transition-colors"
                    onClick={() => setExpanded(prev => { const next = new Set(prev); if (next.has(fy)) next.delete(fy); else next.add(fy); return next; })}
                  >
                    <td className="px-6 py-3 text-white font-bold"><span className="text-gray-500 mr-2">{isExpanded ? '▾' : '▸'}</span>{fiscalYearLabel(fy)}</td>
                    <td colSpan={4} className="px-6 py-3 text-right text-gray-500 text-xs">{months.length} month{months.length === 1 ? '' : 's'} loaded</td>
                    <td className="px-6 py-3 text-right text-gray-500 text-xs">Year to date</td>
                    <td className={`px-6 py-3 text-right font-bold ${latest.fyToDateCents < 0 ? 'text-red-400' : 'text-green-400'}`}><Money cents={latest.fyToDateCents} /></td>
                    <td className="px-6 py-3 text-xs text-gray-500">
                      {months.some(m => m.yearEndClawbackCents > 0) && (
                        <span className="text-red-400">Owed back at year end: <Money cents={months.find(m => m.yearEndClawbackCents > 0)!.yearEndClawbackCents} /></span>
                      )}
                    </td>
                  </tr>
                  {isExpanded && months.map(m => (
                    <tr key={m.month} className="border-b border-gray-800 hover:bg-vlt-dark transition-colors">
                      <td className="px-6 py-3.5 pl-10 text-white font-medium">{monthLabel(m.month)}</td>
                      <td className="px-6 py-3.5 text-right text-gray-300"><Money cents={m.attributedRevenueCents} /></td>
                      <td className="px-6 py-3.5 text-right text-gray-300">{m.hurdleCents === null ? <span className="text-gray-500">none</span> : <Money cents={m.hurdleCents} />}</td>
                      <td className={`px-6 py-3.5 text-right font-semibold ${m.hurdleFactor === null ? 'text-gray-500' : m.hurdleFactor < 0 ? 'text-red-400' : 'text-green-400'}`}>{factorLabel(m.hurdleFactor)}</td>
                      <td className="px-6 py-3.5 text-right text-gray-300"><Money cents={m.splitPayoutCents} /></td>
                      <td className={`px-6 py-3.5 text-right font-bold ${m.monthlyIncentiveCents < 0 ? 'text-red-400' : 'text-green-400'}`}><Money cents={m.monthlyIncentiveCents} /></td>
                      <td className={`px-6 py-3.5 text-right font-bold ${m.fyToDateCents < 0 ? 'text-red-400' : 'text-white'}`}>
                        <Money cents={m.fyToDateCents} />
                        {m.openingCarryCents !== 0 && <span className="block text-[0.625rem] font-normal text-red-400">incl. carry <Money cents={m.openingCarryCents} /></span>}
                      </td>
                      <td className={`px-6 py-3.5 text-xs ${m.monthlyIncentiveCents < 0 || m.yearEndClawbackCents > 0 ? 'text-red-400' : 'text-gray-500'}`}>{m.note}</td>
                    </tr>
                  ))}
                </React.Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default RevenueHurdleSchedule;
